import { useState } from 'react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/AuthContext'
import { toast } from 'sonner'
import { Loader2, Mail, Lock } from 'lucide-react'
import { Link } from 'react-router-dom'
import backdrop from '@/assets/images/backdrop.jpg'

export default function Login() {
  const { login } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)
    let isRateLimited = false;

    try {
      await login(email, password)
      toast.success('Welcome back!')
    } catch (error: unknown) {
      console.error('Login component: Error during login:', error)
      
      // Display the specific error message
      const errorMessage = error instanceof Error ? error.message : 'Failed to login. Please try again.'
      
      // Set the error for the UI
      setError(errorMessage)
      
      // Show toast with the error
      toast.error(errorMessage)
      
      // Add a retry delay if it's a rate limit error
      if (errorMessage.includes('Too many login attempts')) {
        isRateLimited = true;
        // Keep the form disabled for 5 seconds to prevent further rate limiting
        setTimeout(() => {
          setIsLoading(false)
        }, 5000)
      }
    } finally {
      // Only enable the form if not rate limited
      if (!isRateLimited) {
        setIsLoading(false)
      }
    }
  }

  return (
    <div 
      className="min-h-screen flex items-start justify-center relative p-4 pt-20"
      style={{
        backgroundImage: `url(${backdrop})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Add a dark overlay */}
      <div className="absolute inset-0 bg-black/40" />
      
      <Card className="w-full max-w-md relative z-10 backdrop-blur-sm bg-white/80 shadow-xl border-primary/20">
        <CardHeader className="space-y-1 pb-6">
          <CardTitle className="text-2xl text-center font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
            Welcome Back
          </CardTitle>
          <p className="text-center text-sm text-muted-foreground">
            Access your prescriptions securely
          </p>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-destructive/10 text-destructive text-sm p-4 rounded-lg border border-destructive/20">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                  className="pl-10 bg-white/50"
                  autoComplete="email"
                  autoFocus
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  className="pl-10 bg-white/50"
                  autoComplete="current-password"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-primary hover:bg-primary/90 text-white transition-all duration-200 shadow-lg hover:shadow-xl"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </Button>

            <div className="text-center mt-6">
              <Link 
                to="/forgot-password" 
                className="text-sm text-primary hover:text-primary/80 transition-colors duration-200 hover:underline"
              >
                Forgot your password?
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>

      <div className="absolute bottom-4 text-center text-sm text-white z-10">
        © {new Date().getFullYear()} Hydralyfe. All rights reserved.
      </div>
    </div>
  )
}